<template>
  <transition name="slide-left">
    <div v-if="!currentItem" key="items-list" class="flex flex-col w-full">
      <div v-if="itemsList.length > 0" class="w-full">
        <div v-for="(item, index) of itemsList"
          :key="item.id"
          class="nav-item"
          @click="_handleItemClick(item)"
        >
          <div class="nav-item__accent"></div>
          <div class="nav-item__body">
            <div class="flex space-x-3" :class="item.description ? 'items-start' : 'items-center'">
              <b-icon
                pack="fas"
                :icon="_GetIcon(item)"
                class="nav-item__icon"
                :class="item.description ? 'self-start mt-0.5' : 'self-center'"
              />
              <div class="flex flex-col flex-1">
                <span class="nav-item__title">
                  {{ item.name }}
                </span>
                <span
                  v-if="item.description"
                  class="nav-item__desc"
                >
                  {{ item.description }}
                </span>
              </div>
            </div>
            <div class="nav-item__chevron-wrapper">
              <b-icon
                pack="fas"
                icon="chevron-right"
                class="nav-item__chevron"
              />
            </div>
          </div>
        </div>
      </div>
      <empty-panel v-else />
    </div>

    <!-- VISTA: Detalle del Item - Recursión o Componente Dinámico -->
    <div v-else key="item-detail" class="flex flex-col w-full">
      <!-- Si tiene children, hacer recursión -->
      <list-items-view
        v-if="hasChildren"
        :items-list="currentItemChildren"
        :current-item="null"
        @item-click="_handleItemClick"
        @go-back="_handleGoBack"
        @detail-item-click="_handleDetailItemClick"
        @epc-pictograms-reload="$emit('epc-pictograms-reload')"
      />

      <!-- Si no tiene children pero tiene component, cargar componente dinámico -->
      <component
        v-else-if="hasComponent && isValidComponent"
        :is="dynamicComponent"
        v-bind="componentProps"
        @go-back="_handleGoBack"
        @item-click="_handleDetailItemClick"
        @epc-pictograms-reload="$emit('epc-pictograms-reload')"
      />

      <!-- Si no tiene children ni component, o el componente no existe, mostrar mensaje de error -->
      <div v-else class="flex flex-col items-center justify-center py-12 px-4">
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md w-full">
          <div class="flex items-start space-x-3">
            <b-icon pack="fas" icon="exclamation-triangle" class="text-yellow-600 text-xl mt-0.5" />
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-yellow-800 mb-1">
                Componente no definido
              </h3>
              <p class="text-xs text-yellow-700">
                <span v-if="hasComponent && !isValidComponent">
                  El componente "{{ currentItem.component?.name }}" no está registrado o no existe.
                </span>
                <span v-else>
                  No se definió el componente del formulario de configuración para este elemento.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import EmptyPanel from '../empty-panel.vue';
// Importar componentes dinámicos disponibles
import PictogramConfig from '../pictogram/PictogramConfig.vue';

import LayerStatusesConfig from '../layer-statuses/LayerStatusesConfig.vue';
import LayerConfig from '../layers/LayerConfig.vue';
import LayerRatiosConfig from '../layers/LayerRatiosConfig.vue';
// Wrapper con tabs (Resumen + Importar). El importador queda intacto adentro.
import LayerExtraAttributesManager from '../layers/LayerExtraAttributesManager.vue';
import LayerBulkActions from '../layers/LayerBulkActions.vue';
import PictogramActivitySettings from '../linked-entities/PictogramActivitySettings.vue';
import PictogramGeolocationSettings from '../geolocation/PictogramGeolocationSettings.vue';
import PictogramBoqsSettings from '../pictogram/PictogramBoqsSettings.vue';

export default {
  name: 'ListItemsView',

  components: {
    EmptyPanel,
    // Registrar componentes disponibles para carga dinámica
    LayerStatusesConfig,
    LayerConfig,
    LayerRatiosConfig,
    LayerExtraAttributesManager,
    LayerBulkActions,
    PictogramActivitySettings,
    PictogramGeolocationSettings,
    PictogramConfig,
    PictogramBoqsSettings,
  },

  data() {
    return {
      // Mapa de componentes disponibles para carga dinámica
      availableComponents: {
        'LayerStatusesConfig': LayerStatusesConfig,
        'LayerConfig': LayerConfig,
        'LayerRatiosConfig': LayerRatiosConfig,
        // El item de config sigue enviando 'LayerImportExtraAttributes' desde el
        // backend; lo resolvemos al wrapper con tabs (Resumen + Importar).
        'LayerImportExtraAttributes': LayerExtraAttributesManager,
        'LayerBulkActions': LayerBulkActions,
        'PictogramActivitySettings': PictogramActivitySettings,
        'PictogramGeolocationSettings': PictogramGeolocationSettings,
        'PictogramConfig': PictogramConfig,
        'PictogramBoqsSettings': PictogramBoqsSettings,
      }
    };
  },

  props: {
    itemsList: {
      type: Array,
      required: true
    },
    currentItem: {
      type: Object,
      default: null
    }
  },

  computed: {
    currentItemChildren() {
      // Si hay currentItem, obtener sus children para la recursión
      if (this.currentItem) {
        return this.currentItem.children || [];
      }
      return [];
    },

    hasChildren() {
      // Verificar si el currentItem tiene children
      if (!this.currentItem) return false;
      const children = this.currentItem.children || [];
      return children.length > 0;
    },

    hasComponent() {
      // Verificar si el currentItem tiene component definido
      return this.currentItem && this.currentItem.component && this.currentItem.component.name;
    },

    dynamicComponent() {
      // Retornar el componente a cargar dinámicamente
      if (this.currentItem && this.currentItem.component && this.currentItem.component.name) {
        const componentName = this.currentItem.component.name;

        // Buscar el componente en el mapa de componentes disponibles
        let component = this.availableComponents[componentName];

        if (component) {
          return component;
        }

        // Si no se encuentra, intentar buscar por nombre (case-insensitive)
        const componentKey = Object.keys(this.availableComponents).find(
          key => key.toLowerCase() === componentName.toLowerCase()
        );

        if (componentKey) {
          return this.availableComponents[componentKey];
        }

        // También intentar buscar en los componentes registrados como fallback
        component = this.$options.components[componentName];
        if (component) {
          return component;
        }

        // Si no se encuentra, retornar null para mostrar el error
        return null;
      }
      return null;
    },

    componentProps() {
      // Retornar solo las props del componente
      if (this.currentItem && this.currentItem.component && this.currentItem.component.props) {
        return this.currentItem.component.props;
      }
      return {};
    },

    isValidComponent() {
      // Verificar si el componente existe y es válido
      return this.dynamicComponent !== null;
    }
  },

  methods: {
    _handleItemClick(item) {
      this.$emit('item-click', item);
    },

    _handleGoBack() {
      this.$emit('go-back');
    },

    _handleDetailItemClick(item) {
      this.$emit('detail-item-click', item);
    },

    _GetIcon(item) {
      return item.icon ?? 'layer-group'
    }
  }
}
</script>

<style scoped>
/* --- Items de navegación corporativos --- */
.nav-item {
  position: relative;
  display: flex;
  background: #fff;
  border: 1px solid #ADC2EA;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
}

.nav-item:hover {
  border-color: #5577BB;
  box-shadow: 0 2px 8px rgba(85, 119, 187, 0.15);
  transform: translateY(-1px);
}

.nav-item__accent {
  width: 4px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #5577BB, #385DA5);
  transition: width 0.2s ease;
}

.nav-item:hover .nav-item__accent {
  width: 5px;
}

.nav-item__body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.nav-item__icon {
  color: #5577BB;
  font-size: 14px;
}

.nav-item__title {
  font-size: 13px;
  font-weight: 600;
  color: #385DA5;
}

.nav-item__desc {
  font-size: 11px;
  color: #7D99D4;
  margin-top: 2px;
  line-height: 1.3;
}

.nav-item__chevron-wrapper {
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: 0 4px 0 12px;
  margin: -12px -16px -12px 0;
  background: linear-gradient(90deg, transparent, rgba(173, 194, 234, 0.08));
  transition: background 0.2s ease;
}

.nav-item:hover .nav-item__chevron-wrapper {
  background: linear-gradient(90deg, transparent, rgba(85, 119, 187, 0.1));
}

.nav-item__chevron {
  color: #ADC2EA;
  font-size: 16px;
  transition: color 0.2s ease, transform 0.2s ease;
}

.nav-item:hover .nav-item__chevron {
  color: #5577BB;
  transform: translateX(2px);
}

/* --- Transición slide-left --- */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.slide-left-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-enter-to {
  transform: translateX(0);
  opacity: 1;
}

.slide-left-leave {
  transform: translateX(0);
  opacity: 1;
}

.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
</style>
